/**
 * Plain fetch helpers for the admin API (docs/09).
 * Auth is EITHER an admin JWT (`Authorization: Bearer`, from /admin/auth/login)
 * OR the legacy `X-Admin-Key` header. A 401 clears stored credentials and
 * broadcasts UNAUTHORIZED_EVENT so the app drops back to the login screen.
 */

const API_ORIGIN = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';
const API_BASE = API_ORIGIN + '/v1/admin';
const KEY_STORAGE = 'flm.adminKey';
const TOKEN_STORAGE = 'flm.adminToken';
const IDENTITY_STORAGE = 'flm.adminIdentity';

/** Absolute URL for an API-relative media path ("/v1/photos/<id>[?e&s]") — used verbatim. */
export const mediaUrl = (p: string) => `${API_ORIGIN}${p}`;

export const UNAUTHORIZED_EVENT = 'flm:admin-unauthorized';

export function getAdminKey(): string | null {
  return localStorage.getItem(KEY_STORAGE);
}

export function setAdminKey(key: string): void {
  localStorage.setItem(KEY_STORAGE, key);
}

export function clearAdminKey(): void {
  localStorage.removeItem(KEY_STORAGE);
}

export function getAdminToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE);
}

export function setAdminSession(token: string, admin: AdminIdentity): void {
  localStorage.setItem(TOKEN_STORAGE, token);
  localStorage.setItem(IDENTITY_STORAGE, JSON.stringify(admin));
}

export function getAdminIdentity(): AdminIdentity | null {
  const raw = localStorage.getItem(IDENTITY_STORAGE);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AdminIdentity;
  } catch {
    return null;
  }
}

export function updateAdminIdentity(patch: Partial<AdminIdentity>): AdminIdentity | null {
  const current = getAdminIdentity();
  if (!current) return null;
  const next = { ...current, ...patch };
  localStorage.setItem(IDENTITY_STORAGE, JSON.stringify(next));
  return next;
}

/** Drop every stored credential (JWT session + legacy key). */
export function clearAdminAuth(): void {
  localStorage.removeItem(TOKEN_STORAGE);
  localStorage.removeItem(IDENTITY_STORAGE);
  localStorage.removeItem(KEY_STORAGE);
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function authHeaders(): Record<string, string> {
  const token = getAdminToken();
  if (token) return { Authorization: `Bearer ${token}` };
  return { 'X-Admin-Key': getAdminKey() ?? '' };
}

async function errorOf(res: Response): Promise<ApiError> {
  let code: string | undefined;
  let message = `Request failed (HTTP ${res.status}).`;
  try {
    // API envelope is { error: { code, message, requestId } }; tolerate flat bodies too.
    const raw = (await res.json()) as
      | { error?: { code?: string; message?: string }; code?: string; message?: string }
      | null;
    const body = raw?.error ?? raw;
    code = body?.code;
    if (body?.message) message = body.message;
  } catch {
    /* non-JSON error body */
  }
  return new ApiError(message, res.status, code);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(API_BASE + path, {
      ...init,
      headers: {
        ...authHeaders(),
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  } catch {
    throw new ApiError('API unreachable — is the server running?', 0);
  }
  if (res.status === 401) {
    clearAdminAuth();
    window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    throw await errorOf(res);
  }
  if (!res.ok) {
    throw await errorOf(res);
  }
  return (await res.json()) as T;
}

/** Same as request() but never clears stored credentials on 401 (login/bootstrap flows). */
async function requestRaw<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(API_BASE + path, {
      ...init,
      headers: {
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  } catch {
    throw new ApiError('API unreachable — is the server running?', 0);
  }
  if (!res.ok) throw await errorOf(res);
  return (await res.json()) as T;
}

// ---------- admin auth (accounts + TOTP 2FA + RBAC) ----------

export type AdminRole = 'owner' | 'admin' | 'moderator' | 'support';

export interface AdminIdentity {
  id: string | null; // null = legacy-key session
  email: string | null;
  name: string;
  role: AdminRole;
  totpEnrolled: boolean;
  legacy?: boolean;
}

export interface AdminAccount {
  id: string;
  email: string;
  name: string;
  role: AdminRole;
  totpEnrolled: boolean;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface LoginResult {
  token: string;
  admin: AdminIdentity;
}

export const getBootstrapNeeded = () => requestRaw<{ needed: boolean }>('/auth/bootstrap-needed');

/** First-run owner creation — authorized with the legacy key (not yet stored). */
export const bootstrapOwner = (adminKey: string, email: string, name: string, password: string) =>
  requestRaw<LoginResult>('/auth/bootstrap', {
    method: 'POST',
    headers: { 'X-Admin-Key': adminKey },
    body: JSON.stringify({ email, name, password }),
  });

/** Throws ApiError with code 'admin/totp_required' when a TOTP code is needed. */
export const login = (email: string, password: string, totp?: string) =>
  requestRaw<LoginResult>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password, ...(totp ? { totp } : {}) }),
  });

export const getMe = () => request<AdminIdentity>('/auth/me');

export const totpSetup = () =>
  request<{ secret: string; otpauthUrl: string }>('/auth/totp/setup', { method: 'POST', body: '{}' });

export const totpConfirm = (code: string) =>
  request<{ ok: boolean; totpEnrolled: boolean }>('/auth/totp/confirm', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });

export const getAdmins = () => request<ListResponse<AdminAccount>>('/admins');

export const createAdmin = (input: { email: string; name: string; password: string; role: AdminRole }) =>
  request<AdminAccount>('/admins', { method: 'POST', body: JSON.stringify(input) });

export const patchAdmin = (id: string, patch: { role?: AdminRole; isActive?: boolean; password?: string }) =>
  request<AdminAccount>(`/admins/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });

// ---------- contract types ----------

export interface AdminStats {
  users: number;
  profiles: number;
  matches: number;
  messages: number;
  paidPayments: number;
  reportsOpen: number;
}

export type UserStatus = 'active' | 'suspended' | 'banned';

export interface AdminUser {
  id: string;
  status: UserStatus;
  isPremium: boolean;
  premiumPlanId: string | null;
  /** Effective tier shown in the Plan column: active subscription's plan, else 'free'. */
  effectivePlanId: string;
  locale: string | null;
  createdAt: string;
  lastActiveAt: string | null;
  username: string | null;
  displayName: string | null;
  gender: string | null;
  verified: boolean;
  city: string | null;
  /** API-relative avatar (cover photo stream or Telegram photo); pass through mediaUrl(). */
  photoUrl: string | null;
}

/** Composite dashboard payload — every figure is a real aggregate (see admin-dashboard.controller). */
export interface DashboardData {
  env: string;
  botOnline: boolean;
  cards: {
    totalUsers: number;
    usersToday: number;
    activeToday: number;
    matchesToday: number;
    revenue30dTon: number;
    premiumSubs: number;
    premiumSubsThisWeek: number;
    openReports: number;
    urgentReports: number;
  };
  signupsMatches30d: { date: string; signups: number; matches: number }[];
  revenueByWeek: { label: string; ton: number }[];
  revenueSplit30d: { subscription: number; gift: number; boost: number; event_deposit: number };
  revenue30dTon: number;
  liveActivity: { kind: 'payment' | 'match' | 'report'; text: string; at: string }[];
  newestMembers: {
    id: string;
    name: string;
    username: string | null;
    city: string | null;
    plan: string;
    status: UserStatus;
    joinedAt: string;
    lastActiveAt: string | null;
    photoUrl: string | null;
  }[];
}

export interface NavBadges {
  reportsOpen: number;
  verificationPending: number;
}

export interface AdminPayment {
  id: string;
  status: string;
  purpose: string;
  provider: string;
  currency: string;
  amountTon: string | null;
  memo: string | null;
  txHash: string | null;
  createdAt: string;
  user: string;
}

/** Reporter/reported user context attached to each report row. */
export interface ReportUserRef {
  id: string;
  displayName: string | null;
  username: string | null;
  status: string | null; // null = user no longer exists
}

/** Report rows (raw fields kept; reporter/reported context added server-side). */
export interface AdminReport {
  id: string;
  reporterUserId?: string;
  targetUserId?: string;
  reason?: string;
  status?: string;
  detail?: string | null;
  createdAt?: string;
  reporter?: ReportUserRef;
  reported?: ReportUserRef;
}

/** A user's photo as seen by moderation (url is API-relative — pass through mediaUrl()). */
export interface AdminPhoto {
  id: string;
  url: string;
  isCover: boolean;
  position: number;
  moderationStatus: string;
  createdAt: string;
}

/** ReportStatus enum values ('actioned' is what the UI's Resolve maps to). */
export type ReportStatus = 'open' | 'reviewing' | 'actioned' | 'dismissed';

/** Per-tier entitlements stored on Plan.entitlements (admin-editable; null until seeded).
    The 'free' plan additionally carries likesPerDay (the default-tier swipe cap). */
export interface PlanEntitlements {
  unlimitedLikes?: boolean;
  /** Free-tier swipe cap (only meaningful on the 'free' default tier). */
  likesPerDay?: number;
  sparksPerDay?: number;
  boostsPerWeek?: number;
  canRewind?: boolean;
  seeLikers?: boolean;
  messageBeforeMatch?: boolean;
  /** Map: reveal faces + open profiles near you (local). */
  mapReveal?: boolean;
  /** Passport: see profiles worldwide + change location on the map ("travel"). */
  passport?: boolean;
}

export interface AdminPlan {
  id: string;
  name: string;
  priceTon: string; // whole/fractional TON as decimal string (nanoTON BigInt server-side)
  priceUsd: number;
  priceStars: number;
  /** Yearly prices (null until set) — TON as a decimal string, USD/Stars as ints. */
  priceTonYearly: string | null;
  priceUsdYearly: number | null;
  priceStarsYearly: number | null;
  periodMonths: number;
  isFeatured: boolean;
  /** true for the non-purchasable 'free' default tier (hide price inputs). */
  isFree: boolean;
  /** Plan icon key from the miniapp Icon set ('zap'|'activity'|'infinity'|'flame'|…). */
  icon: string | null;
  perks: string[];
  entitlements: PlanEntitlements | null;
}

export interface AdminGift {
  id: string;
  name: string;
  priceTon: string;
  priceStars: number;
}

export interface AdminEvent {
  id: string;
  title: string;
  venue: string;
  startsAt: string;
  goingCount: number;
  depositTon: string | null;
  createdAt: string;
}

export interface ListResponse<T> {
  data: T[];
  total: number;
  /** 1-based page + page size (present on paginated endpoints). */
  page?: number;
  pageSize?: number;
}

/** Build a `?a=1&b=2` query string from defined params (skips undefined/empty). */
function qs(params: Record<string, string | number | undefined>): string {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

// ---------- endpoints ----------

export const getStats = () => request<AdminStats>('/stats');

export const getDashboard = () => request<DashboardData>('/dashboard');

export const getNavBadges = () => request<NavBadges>('/nav-badges');

export interface UsersQuery {
  search?: string;
  page?: number;
  pageSize?: number;
  gender?: string;
  status?: string;
  verified?: 'true' | 'false' | '';
  sort?: string;
  order?: 'asc' | 'desc';
}
export const getUsers = (q: UsersQuery = {}) =>
  request<ListResponse<AdminUser>>(`/users${qs({ ...q })}`);

export interface UserPatch {
  status?: UserStatus;
  premiumDays?: number;
  /**
   * Tier for a premium grant: paid tiers (spark|pulse|infinity) grant/extend
   * premium; 'free' is a DOWNGRADE (cancels active subs + clears the flag, like
   * revokePremium — premiumDays is ignored).
   */
  planId?: string;
  revokePremium?: boolean;
}

export const patchUser = (id: string, patch: UserPatch) =>
  request<{ id: string; status: UserStatus; isPremium: boolean; premiumPlanId: string | null }>(`/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

export const patchUserStatus = (id: string, status: UserStatus) => patchUser(id, { status });

export const deleteUser = (id: string) =>
  request<{ ok: boolean; deleted: string }>(`/users/${id}`, { method: 'DELETE' });

/** Clear a test user's swipes + matches (+ blocks) but keep the account & profile. */
export const resetUser = (id: string) =>
  request<{ ok: boolean; cleared: { swipes: number; matches: number } }>(`/users/${id}/reset`, {
    method: 'POST',
  });

export const getUserPhotos = (id: string) => request<{ items: AdminPhoto[] }>(`/users/${id}/photos`);

// ---------- full user detail (GET /users/:id — everything admin can see) ----------

export interface UserDetailIdentity {
  id: string;
  status: UserStatus;
  isPremium: boolean;
  premiumPlanId: string | null;
  locale: string | null;
  ageVerified18: boolean;
  createdAt: string;
  lastActiveAt: string | null;
}

export interface UserDetailTelegram {
  tgUserId: string; // BigInt server-side — always a string over JSON
  username: string | null;
  firstName: string | null;
  languageCode: string | null;
  allowsWriteToPm: boolean;
  photoUrl: string | null;
}

export interface UserDetailProfile {
  displayName: string;
  birthDate: string; // YYYY-MM-DD
  age: number | null;
  gender: string;
  bio: string | null;
  job: string | null;
  isVerified: boolean;
  isHidden: boolean;
  boostedUntil: string | null;
  interests: { slug: string; labelKey: string }[];
  hasVoiceIntro: boolean;
  voiceDurationSecs: number | null;
  // coarse place + optional dating attributes (any may be null)
  country: string | null;
  city: string | null;
  heightCm: number | null;
  weightKg: number | null;
  bodyType: string | null;
  smoking: string | null;
  drinking: string | null;
  exercise: string | null;
  diet: string | null;
  pets: string | null;
  languages: string[];
  education: string | null;
  religion: string | null;
  zodiac: string | null;
  relationshipGoal: string | null;
  wantsChildren: string | null;
}

export interface UserDetailPreference {
  showGender: string;
  ageMin: number;
  ageMax: number;
  distanceKm: number;
}

/** exactLat/exactLng are the UNFUZZED coordinates (admin-only; members see ±250m fuzz). */
export interface UserDetailLocation {
  exactLat: number | null;
  exactLng: number | null;
  geohash5: string;
  precisionM: number;
  source: string;
  updatedAt: string;
}

export interface UserDetailCounts {
  photos: number;
  swipesYes: number;
  swipesPass: number;
  likesReceived: number;
  matches: number;
  messagesSent: number;
  reportsAgainst: number;
  blocksGiven: number;
  blocksReceived: number;
  paymentsPaid: number;
  totalPaidTon: string;
}

export interface UserDetailSubscription {
  id: string;
  planId: string;
  planName: string | null;
  status: string;
  startedAt: string;
  expiresAt: string;
}

export interface UserDetailPayment {
  id: string;
  status: string;
  purpose: string;
  provider: string;
  currency: string;
  amountMinor: string; // BigInt server-side — string over JSON
  amountTon: string | null;
  createdAt: string;
}

export interface AdminUserDetail {
  user: UserDetailIdentity;
  telegram: UserDetailTelegram | null;
  profile: UserDetailProfile | null;
  preference: UserDetailPreference | null;
  location: UserDetailLocation | null;
  counts: UserDetailCounts;
  subscriptions: UserDetailSubscription[];
  recentPayments: UserDetailPayment[];
  onboarding: { step: number; completed: boolean };
}

export const getUserDetail = (id: string) => request<AdminUserDetail>(`/users/${id}`);

export const deletePhoto = (photoId: string) =>
  request<{ ok: boolean; deleted: string }>(`/photos/${photoId}`, { method: 'DELETE' });

export const setPhotoCover = (photoId: string) =>
  request<{ ok: boolean; coverPhotoId: string }>(`/photos/${photoId}/cover`, { method: 'POST' });

export const getPayments = (page?: number, pageSize?: number) =>
  request<ListResponse<AdminPayment>>(`/payments${qs({ page, pageSize })}`);

export const getReports = (page?: number, pageSize?: number) =>
  request<ListResponse<AdminReport>>(`/reports${qs({ page, pageSize })}`);

export const patchReport = (id: string, status: ReportStatus) =>
  request<{ id: string; status: ReportStatus }>(`/reports/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });

// ---------- catalog (plans / gifts / events) ----------

export interface PlanPatch {
  name?: string;
  priceTon?: string;
  priceUsd?: number;
  priceStars?: number;
  /** Yearly prices — null/'' clears them (no yearly option for that tier). */
  priceTonYearly?: string | null;
  priceUsdYearly?: number | null;
  priceStarsYearly?: number | null;
  /** Plan icon key (null/'' clears it). */
  icon?: string | null;
  isFeatured?: boolean;
  perks?: string[];
  /** Partial entitlement edit — merged server-side over the stored entitlements. */
  entitlements?: PlanEntitlements;
}

export const getPlans = () => request<ListResponse<AdminPlan>>('/plans');

export const patchPlan = (id: string, patch: PlanPatch) =>
  request<AdminPlan>(`/plans/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });

export interface GiftPatch {
  name?: string;
  priceTon?: string;
  priceStars?: number;
}

export const getGifts = () => request<ListResponse<AdminGift>>('/gifts');

export const patchGift = (id: string, patch: GiftPatch) =>
  request<AdminGift>(`/gifts/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });

export interface EventInput {
  title?: string;
  venue?: string;
  startsAt?: string; // ISO
  depositTon?: string | null; // null clears the deposit
}

export const getEvents = () => request<ListResponse<AdminEvent>>('/events');

export const createEvent = (input: Required<Pick<EventInput, 'title' | 'venue' | 'startsAt'>> & EventInput) =>
  request<AdminEvent>('/events', { method: 'POST', body: JSON.stringify(input) });

export const patchEvent = (id: string, patch: EventInput) =>
  request<AdminEvent>(`/events/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });

export const deleteEvent = (id: string) =>
  request<{ ok: boolean; deleted: string }>(`/events/${id}`, { method: 'DELETE' });

// ---------- seed importer (fake/imported users) ----------

export interface SeedSource {
  source: string;
  total: number;
  byCountry: Record<string, number>;
}
export interface SeedBatch {
  id: string;
  source: string;
  status: 'running' | 'done' | 'failed' | 'cancelled';
  requested: number;
  imported: number;
  skipped: number;
  failed: number;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface SeedImportInput {
  source: string;
  countries?: string[];
  gender?: 'man' | 'woman';
  minPhotos?: number;
  limit?: number;
  ageMin?: number;
  ageMax?: number;
  city?: string;
}
export interface SeedFilter {
  source?: string;
  countries?: string[];
  gender?: 'man' | 'woman';
  city?: string;
  ageMin?: number;
  ageMax?: number;
  batch?: string;
  status?: 'active' | 'suspended';
}
export type SeedOp = 'deactivate' | 'activate' | 'delete';

export const getSeedSources = () => request<{ dir: string; sources: SeedSource[] }>('/seed/sources');
export const startSeedImport = (input: SeedImportInput) =>
  request<{ batchId: string }>('/seed/import', { method: 'POST', body: JSON.stringify(input) });
export const getSeedBatches = () => request<SeedBatch[]>('/seed/batches');
export const getSeedBatch = (id: string) => request<SeedBatch>(`/seed/batches/${encodeURIComponent(id)}`);
export const cancelSeedImport = (id: string) =>
  request<{ cancelled: number }>(`/seed/batches/${encodeURIComponent(id)}/cancel`, { method: 'POST' });
export const countSeeds = (f: SeedFilter) => {
  const q = new URLSearchParams();
  if (f.source) q.set('source', f.source);
  if (f.countries?.length) q.set('countries', f.countries.join(','));
  if (f.gender) q.set('gender', f.gender);
  if (f.city?.trim()) q.set('city', f.city.trim());
  if (f.ageMin != null) q.set('ageMin', String(f.ageMin));
  if (f.ageMax != null) q.set('ageMax', String(f.ageMax));
  if (f.batch) q.set('batch', f.batch);
  if (f.status) q.set('status', f.status);
  return request<{ count: number }>(`/seed/count?${q.toString()}`);
};
/** op='delete' → { deleted }; 'deactivate'/'activate' → { affected }. */
export const manageSeeds = (op: SeedOp, f: SeedFilter) =>
  request<{ deleted?: number; affected?: number }>('/seed/manage', { method: 'POST', body: JSON.stringify({ op, ...f }) });

// ---------- settings (runtime config; secret values arrive masked) ----------

export interface AdminSetting {
  key: string;
  isSecret: boolean;
  updatedAt: string;
  /** Present only on non-secret rows. */
  value?: string;
  /** Secret rows: the full value never leaves the server. */
  masked?: true;
  hint?: string; // '••••' + last 4
}

export const getSettings = () => request<ListResponse<AdminSetting>>('/settings');

export interface BlurStatus { missing: number; running: boolean; done: number; failed: number }
export const getBlurStatus = () => request<BlurStatus>('/media/blur-status');
export const startBlurBackfill = () => request<Partial<BlurStatus>>('/media/blur-backfill', { method: 'POST' });

export const putSetting = (key: string, value: string, isSecret?: boolean) =>
  request<AdminSetting>(`/settings/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: JSON.stringify({ value, ...(isSecret !== undefined ? { isSecret } : {}) }),
  });

export const deleteSetting = (key: string) =>
  request<{ ok: boolean; deleted: string }>(`/settings/${encodeURIComponent(key)}`, {
    method: 'DELETE',
  });

/** Live validation of the TON payment config (wallet + network + indexer). */
export interface TonConfigTest {
  ok: boolean;
  network: string;
  address: string | null;
  addressValid: boolean;
  networkMatch: boolean;
  toncenter: string | null;
  indexerOk: boolean;
  indexerStatus?: number;
  message: string;
}
export const testTonConfig = () => request<TonConfigTest>('/payments/ton-test');

/** Upload the TON Connect wallet-dialog icon (multipart). Returns the public URL. */
export async function uploadBrandIcon(file: File, size: number): Promise<{ url: string; size: number }> {
  const fd = new FormData();
  fd.append('file', file);
  let res: Response;
  try {
    // No Content-Type header — the browser sets the multipart boundary.
    res = await fetch(`${API_BASE}/payments/brand-icon?size=${size}`, { method: 'POST', headers: authHeaders(), body: fd });
  } catch {
    throw new ApiError('API unreachable — is the server running?', 0);
  }
  if (res.status === 401) {
    clearAdminAuth();
    window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    throw await errorOf(res);
  }
  if (!res.ok) throw await errorOf(res);
  return (await res.json()) as { url: string; size: number };
}

export const clearBrandIcon = () => request<{ ok: boolean }>('/payments/brand-icon', { method: 'DELETE' });

// ---------- AI vision connectivity test ----------

/** Result of a live probe against the saved vision provider/key/endpoint. */
export interface VisionProbe {
  ok: boolean;
  provider: string;
  model?: string;
  endpoint?: string;
  status?: number;
  latencyMs?: number;
  message: string;
  sample?: {
    isPerson: boolean | null;
    explicit: boolean | null;
    estimatedGender: 'man' | 'woman' | 'unknown' | null;
    notes?: string;
  };
}

/** Server-side test of the configured vision provider — the API key stays server-side. */
export const testVision = () => request<VisionProbe>('/ai/test-vision', { method: 'POST' });

// ---------- translations (Mini App UI strings) ----------

export interface AdminTranslation {
  id: string;
  locale: string;
  key: string;
  value: string;
  updatedAt: string;
}

export const getTranslations = (locale: string) =>
  request<ListResponse<AdminTranslation>>(`/translations?locale=${encodeURIComponent(locale)}`);

export const putTranslations = (locale: string, entries: Record<string, string>) =>
  request<{ ok: boolean; locale: string; upserted: number }>('/translations', {
    method: 'PUT',
    body: JSON.stringify({ locale, entries }),
  });

export const deleteTranslation = (id: string) =>
  request<{ ok: boolean; deleted: string }>(`/translations/${id}`, { method: 'DELETE' });

// ---------- photo moderation queue ----------

export type ModerationStatus = 'pending' | 'approved' | 'rejected' | 'flagged';
export type ModerationFilter = ModerationStatus | 'all';

/**
 * AI moderation findings attached to a photo by the backend's vision/checks
 * pipeline. All fields optional + best-effort — render defensively whatever is
 * present. Findings never auto-reject; they only annotate the queue for review.
 */
export interface ModerationFindings {
  /** Looks like another account's photo. */
  duplicate?: boolean;
  /** Possible explicit / NSFW content. */
  explicit?: boolean;
  /** A human face was detected (false → "no face detected"). */
  isPerson?: boolean;
  /** Estimated gender (e.g. 'man' | 'woman') — flagged on mismatch only. */
  estimatedGender?: string;
  /** Free-text note from the model. */
  notes?: string;
}

/** Queue row: photo + owner context. url is API-relative (pass through mediaUrl()). */
export interface ModerationPhoto {
  id: string;
  url: string;
  moderationStatus: ModerationStatus;
  position: number;
  createdAt: string;
  user: { id: string; displayName: string | null; username: string | null };
  /** AI findings (optional — absent until the backend pipeline has run). */
  moderationFindings?: ModerationFindings | null;
}

export const getModerationPhotos = (status: ModerationFilter = 'all', page = 1, take = 50) =>
  request<{ items: ModerationPhoto[]; total: number; page: number; pageSize: number }>(
    `/moderation/photos${qs({ status, page, take })}`,
  );

export const approveModerationPhoto = (id: string) =>
  request<{ id: string; moderationStatus: ModerationStatus }>(`/moderation/photos/${id}/approve`, {
    method: 'POST',
  });

/** Reject = permanent delete (DB + storage) + queued bot DM to the owner. */
export const rejectModerationPhoto = (id: string) =>
  request<{ ok: boolean; deleted: string }>(`/moderation/photos/${id}/reject`, { method: 'POST' });

// ---------- broadcasts (marketing bot DMs to all / segment / specific users) ----------

export type BroadcastAudience = 'all' | 'segment' | 'users';
export type BroadcastMediaType = 'photo' | 'video';

/** Audience criteria — interpreted per `audience` (segment vs explicit users). */
export interface BroadcastFilter {
  /** segment: true = premium-only, false = free-only. */
  premium?: boolean;
  /** segment: restrict by account status. */
  status?: UserStatus;
  /** users: explicit user ids. */
  userIds?: string[];
  /** users: telegram usernames (resolved to ids server-side; leading @ optional). */
  usernames?: string[];
}

export interface CreateBroadcastInput {
  audience: BroadcastAudience;
  filter?: BroadcastFilter;
  /** Telegram-HTML body (subset: <b> <i> <u> <s> <a href> <code> <pre>). */
  bodyHtml?: string;
  mediaType?: BroadcastMediaType;
  mediaUrl?: string;
  /** Overrides the default open-app button label. */
  buttonLabel?: string;
}

export interface AdminBroadcast {
  id: string;
  audience: BroadcastAudience;
  filter: BroadcastFilter | null;
  bodyHtml: string;
  mediaType: BroadcastMediaType | null;
  mediaUrl: string | null;
  buttonLabel: string | null;
  status: string; // queued | sending | done | failed
  totalCount: number;
  sentCount: number;
  failedCount: number;
  createdByAdminId: string | null;
  createdAt: string;
}

/** Created row + the live enqueue tally (how many jobs the queue accepted/rejected). */
export type CreateBroadcastResult = AdminBroadcast & { enqueued: number; failed: number };

export const getBroadcasts = () => request<ListResponse<AdminBroadcast>>('/broadcasts');

export const createBroadcast = (input: CreateBroadcastInput) =>
  request<CreateBroadcastResult>('/broadcasts', { method: 'POST', body: JSON.stringify(input) });
